import { X, AlertCircle, ShieldAlert, Trash2, Loader2 } from 'lucide-react';
import { GRAD, fg, fgMuted, surface, border, muted } from './profile-shared';

interface ProfileModalsProps {
  // Lightbox
  lightbox: string | null;
  setLightbox: (v: string | null) => void;
  // Delete account
  deleteOpen: boolean;
  setDeleteOpen: (v: boolean) => void;
  deleteConfirm: string;
  setDeleteConfirm: (v: string) => void;
  deleteError: string;
  setDeleteError: (v: string) => void;
  deleting: boolean;
  onDeleteAccount: () => void;
  userLabel: string;
}

export default function ProfileModals({
  lightbox, setLightbox,
  deleteOpen, setDeleteOpen, deleteConfirm, setDeleteConfirm, deleteError, setDeleteError,
  deleting, onDeleteAccount, userLabel,
}: ProfileModalsProps) {
  return (
    <>
      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.85)' }}
          onClick={() => setLightbox(null)}>
          <button
            className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full"
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}
            onClick={() => setLightbox(null)}>
            <X size={18} />
          </button>
          <img
            src={lightbox}
            alt="Shared photo"
            className="max-h-[90vh] max-w-full rounded-2xl object-contain shadow-2xl"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}

      {/* Delete account confirmation */}
      {deleteOpen && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.6)' }}
          onClick={() => { if (!deleting) setDeleteOpen(false); }}>
          <div
            className="w-full max-w-md rounded-2xl p-6"
            style={{ background: surface, border: '1px solid hsla(0,70%,55%,0.35)' }}
            onClick={e => e.stopPropagation()}>
            <div className="mb-4 flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ background: 'hsla(0,70%,55%,0.12)', color: 'hsl(0 70% 55%)' }}>
                <ShieldAlert size={18} />
              </span>
              <div>
                <h3 className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>Delete your account?</h3>
                <p className="text-xs" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>This action is permanent and cannot be undone.</p>
              </div>
            </div>

            <p className="mb-2 text-sm" style={{ color: fg, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              To confirm, type your name <span style={{ fontWeight: 700, color: GRAD }}>{userLabel}</span> below.
            </p>
            <input
              type="text"
              value={deleteConfirm}
              onChange={e => { setDeleteConfirm(e.target.value); setDeleteError(''); }}
              placeholder="Type your name to confirm"
              autoComplete="off"
              className="w-full rounded-xl px-4 py-3 text-sm outline-none"
              style={{ background: muted, border: `1px solid ${border}`, color: fg, fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            />

            {deleteError && (
              <div className="mt-3 flex items-center gap-2 text-xs" style={{ color: 'hsl(0 70% 55%)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                <AlertCircle size={13} className="shrink-0" /> {deleteError}
              </div>
            )}

            <div className="mt-5 flex gap-2">
              <button
                type="button"
                disabled={deleting}
                onClick={() => setDeleteOpen(false)}
                className="flex-1 rounded-xl py-3 text-sm font-semibold transition-all hover:opacity-90 disabled:opacity-60"
                style={{ background: muted, border: `1px solid ${border}`, color: fg, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Cancel
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={onDeleteAccount}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold transition-all hover:opacity-90 disabled:opacity-60"
                style={{ background: 'hsl(0 70% 55%)', color: '#fff', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {deleting ? <><Loader2 size={15} className="animate-spin" /> Deleting…</> : <><Trash2 size={15} /> Delete forever</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
